/**
 * Vendor Form Page Component
 *
 * Unified form for creating and editing vendors with:
 * - TanStack Form for form state management
 * - Zod schema validation
 * - shadcn/ui components for UI
 * - Type-safe form handling
 * - Automatic mode detection (create vs edit)
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { FormField, getFieldError } from '@/components/form/FormField';
import {
  useVendor,
  useCreateVendor,
  useUpdateVendor,
} from '../services/vendorService';
import type { CreateVendor, UpdateVendor } from '../types';

/**
 * Zod validation schema for vendor form
 *
 * Defines validation rules for all vendor fields with appropriate
 * error messages and constraints.
 */
const vendorSchema = z.object({
  name: z
    .string()
    .min(1, 'Vendor name is required')
    .max(200, 'Vendor name must be less than 200 characters'),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional(),
  website: z
    .string()
    .url('Must be a valid URL')
    .optional()
    .or(z.literal('')),
  primaryContactName: z
    .string()
    .min(1, 'Primary contact name is required')
    .max(100, 'Contact name must be less than 100 characters'),
  primaryContactEmail: z
    .string()
    .email('Must be a valid email address')
    .min(1, 'Primary contact email is required'),
  primaryContactPhone: z
    .string()
    .max(20, 'Phone number must be less than 20 characters')
    .optional(),
  address: z
    .string()
    .max(200, 'Address must be less than 200 characters')
    .optional(),
  city: z
    .string()
    .max(100, 'City must be less than 100 characters')
    .optional(),
  state: z
    .string()
    .max(100, 'State must be less than 100 characters')
    .optional(),
  country: z
    .string()
    .max(100, 'Country must be less than 100 characters')
    .optional(),
  postalCode: z
    .string()
    .max(20, 'Postal code must be less than 20 characters')
    .optional(),
  criticalityRating: z
    .number()
    .min(1, 'Criticality rating must be between 1 and 5')
    .max(5, 'Criticality rating must be between 1 and 5'),
  notes: z
    .string()
    .max(2000, 'Notes must be less than 2000 characters')
    .optional(),
  isActive: z.boolean(),
}) satisfies z.ZodType<CreateVendor>;

/**
 * Vendor Form Page Component
 */
const VendorForm: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const vendorId = params.id;
  const isEditMode = Boolean(vendorId);

  // Fetch existing vendor data if in edit mode
  const { data: existingVendor, isLoading: isLoadingVendor } = useVendor(
    vendorId || '',
    {
      enabled: isEditMode,
    }
  );

  // Mutations
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();

  /**
   * Initialize form with TanStack Form
   */
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      website: '',
      primaryContactName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      criticalityRating: 3,
      notes: '',
      isActive: true,
    } as CreateVendor,
    validators: {
      onChange: vendorSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        if (isEditMode && vendorId) {
          // Update existing vendor
          const updateData: UpdateVendor = {
            ...value,
            // Convert empty strings to undefined for optional fields
            description: value.description || undefined,
            website: value.website || undefined,
            primaryContactPhone: value.primaryContactPhone || undefined,
            address: value.address || undefined,
            city: value.city || undefined,
            state: value.state || undefined,
            country: value.country || undefined,
            postalCode: value.postalCode || undefined,
            notes: value.notes || undefined,
          };
          await updateVendor.mutateAsync({ id: vendorId, data: updateData });
          navigate({ to: `/vendors/${vendorId}` });
        } else {
          // Create new vendor
          const createData: CreateVendor = {
            ...value,
            // Convert empty strings to undefined for optional fields
            description: value.description || undefined,
            website: value.website || undefined,
            primaryContactPhone: value.primaryContactPhone || undefined,
            address: value.address || undefined,
            city: value.city || undefined,
            state: value.state || undefined,
            country: value.country || undefined,
            postalCode: value.postalCode || undefined,
            notes: value.notes || undefined,
          };
          const newVendor = await createVendor.mutateAsync(createData);
          navigate({ to: `/vendors/${newVendor.id}` });
        }
      } catch (error) {
        console.error('Failed to save vendor:', error);
      }
    },
  });

  /**
   * Populate form when editing existing vendor
   */
  useEffect(() => {
    if (existingVendor && isEditMode) {
      form.setFieldValue('name', existingVendor.name);
      form.setFieldValue('description', existingVendor.description || '');
      form.setFieldValue('website', existingVendor.website || '');
      form.setFieldValue('primaryContactName', existingVendor.primaryContactName);
      form.setFieldValue('primaryContactEmail', existingVendor.primaryContactEmail);
      form.setFieldValue('primaryContactPhone', existingVendor.primaryContactPhone || '');
      form.setFieldValue('address', existingVendor.address || '');
      form.setFieldValue('city', existingVendor.city || '');
      form.setFieldValue('state', existingVendor.state || '');
      form.setFieldValue('country', existingVendor.country || '');
      form.setFieldValue('postalCode', existingVendor.postalCode || '');
      form.setFieldValue('criticalityRating', existingVendor.criticalityRating);
      form.setFieldValue('notes', existingVendor.notes || '');
      form.setFieldValue('isActive', existingVendor.isActive);
    }
  }, [existingVendor, isEditMode]);

  /**
   * Loading state for edit mode
   */
  if (isEditMode && isLoadingVendor) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  /**
   * Error state for edit mode
   */
  if (isEditMode && !existingVendor && !isLoadingVendor) {
    return (
      <div className="max-w-3xl mx-auto py-8 px-4">
        <Alert variant="destructive">Vendor not found</Alert>
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/vendors' })}
          className="mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Vendors
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* Page Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate({ to: isEditMode ? `/vendors/${vendorId}` : '/vendors' })}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isEditMode ? 'Back to Vendor Details' : 'Back to Vendors'}
        </Button>

        <h1 className="text-3xl font-semibold text-grey-900">
          {isEditMode ? 'Edit Vendor' : 'New Vendor'}
        </h1>
      </div>

      {/* Form Errors */}
      {(createVendor.isError || updateVendor.isError) && (
        <Alert variant="destructive" className="mb-6">
          Failed to save vendor. Please check your input and try again.
        </Alert>
      )}

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        {/* Basic Information Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <form.Field
                name="name"
                children={(field) => (
                  <FormField label="Vendor Name" required error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12">
              <form.Field
                name="description"
                children={(field) => (
                  <FormField label="Description" error={getFieldError(field.state.meta.errors)}>
                    <Textarea
                      rows={3}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 md:col-span-6">
              <form.Field
                name="website"
                children={(field) => (
                  <FormField label="Website" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      type="url"
                      placeholder="https://example.com"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 md:col-span-6">
              <form.Field
                name="criticalityRating"
                children={(field) => (
                  <div className="space-y-1.5">
                    <Label className="text-grey-500 text-sm">Criticality Rating</Label>
                    <div className="flex items-center gap-4">
                      <Slider
                        value={[field.state.value]}
                        onValueChange={(val) => field.handleChange(val[0])}
                        min={1}
                        max={5}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-lg font-semibold text-primary min-w-[30px] text-center">
                        {field.state.value}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-grey-400 px-1">
                      <span>1</span>
                      <span>2</span>
                      <span>3</span>
                      <span>4</span>
                      <span>5</span>
                    </div>
                  </div>
                )}
              />
            </div>

            <div className="col-span-12">
              <form.Field
                name="isActive"
                children={(field) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={field.state.value}
                      onCheckedChange={field.handleChange}
                      id="isActive"
                    />
                    <Label htmlFor="isActive">Active Vendor</Label>
                  </div>
                )}
              />
            </div>
          </div>
        </Card>

        {/* Contact Information Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Contact Information
          </h2>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <form.Field
                name="primaryContactName"
                children={(field) => (
                  <FormField label="Primary Contact Name" required error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <form.Field
                name="primaryContactEmail"
                children={(field) => (
                  <FormField label="Primary Contact Email" required error={getFieldError(field.state.meta.errors)}>
                    <Input
                      type="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <form.Field
                name="primaryContactPhone"
                children={(field) => (
                  <FormField label="Primary Contact Phone" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>
          </div>
        </Card>

        {/* Address Information Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Address Information
          </h2>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <form.Field
                name="address"
                children={(field) => (
                  <FormField label="Street Address" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <form.Field
                name="city"
                children={(field) => (
                  <FormField label="City" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <form.Field
                name="state"
                children={(field) => (
                  <FormField label="State/Province" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <form.Field
                name="country"
                children={(field) => (
                  <FormField label="Country" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="col-span-12 sm:col-span-6 md:col-span-3">
              <form.Field
                name="postalCode"
                children={(field) => (
                  <FormField label="Postal/ZIP Code" error={getFieldError(field.state.meta.errors)}>
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormField>
                )}
              />
            </div>
          </div>
        </Card>

        {/* Notes Section */}
        <Card className="p-6 mb-6">
          <h2 className="text-lg font-semibold text-grey-900 mb-4">
            Notes
          </h2>
          <form.Field
            name="notes"
            children={(field) => (
              <FormField label="Internal Notes" error={getFieldError(field.state.meta.errors)}>
                <Textarea
                  rows={4}
                  placeholder="Add any additional notes about this vendor..."
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormField>
            )}
          />
        </Card>

        {/* Form Actions */}
        <Card className="p-6">
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate({ to: isEditMode ? `/vendors/${vendorId}` : '/vendors' })
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createVendor.isPending || updateVendor.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {createVendor.isPending || updateVendor.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update Vendor'
                : 'Create Vendor'}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
};

export default VendorForm;
